import React, { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import WhyThisMatters from "../WhyThisMatters";

const inputCls = "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

const Field = ({ label, hint, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <Label className="label-tiny">{label}</Label>
    {hint && <p className="text-xs text-slate-500 -mt-1">{hint}</p>}
    {children}
  </div>
);

export default function QuestionnaireTab({ caseData, save }) {
  const [q, setQ] = useState({
    claim_type: "",
    summary: "",
    damages_amount: "",
    damages_description: "",
    incident_location: "",
    relief_requested: "",
    ...(caseData.questionnaire || {}),
  });

  const handleSave = () => save({ questionnaire: q });

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="font-serif-display text-3xl text-slate-900">Guided Questionnaire</h2>
          <span className="label-tiny">Step 2 of 8</span>
        </div>
        <p className="text-slate-600 max-w-2xl">Plain-English questions about your matter. Your answers seed the complaint.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Type of Claim" hint="What kind of legal wrong are you alleging?" testid="field-claim-type">
          <Select value={q.claim_type} onValueChange={(v) => setQ({ ...q, claim_type: v })}>
            <SelectTrigger className={inputCls} data-testid="qq-claim-type"><SelectValue placeholder="Select claim type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Breach of Contract">Breach of Contract</SelectItem>
              <SelectItem value="Negligence">Negligence</SelectItem>
              <SelectItem value="Fraud">Fraud / Misrepresentation</SelectItem>
              <SelectItem value="Defamation">Defamation</SelectItem>
              <SelectItem value="Employment Discrimination">Employment Discrimination</SelectItem>
              <SelectItem value="Intentional Infliction of Emotional Distress">Intentional Infliction of Emotional Distress</SelectItem>
              <SelectItem value="Unjust Enrichment">Unjust Enrichment</SelectItem>
              <SelectItem value="Conversion">Conversion</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field label="Where did the incident occur?" hint="City, county, and state." testid="field-incident-location">
          <Input value={q.incident_location} onChange={(e) => setQ({ ...q, incident_location: e.target.value })} placeholder="San Francisco, San Francisco County, California" className={inputCls} data-testid="qq-incident-location" />
        </Field>

        <Field label="Total Damages Sought (approx.)" hint="Numerical estimate, if known" testid="field-damages-amount">
          <Input value={q.damages_amount} onChange={(e) => setQ({ ...q, damages_amount: e.target.value })} placeholder="$75,000" className={inputCls} data-testid="qq-damages-amount" />
        </Field>

        <Field label="Relief Requested (high-level)" hint="What outcome do you want from the court?" testid="field-relief">
          <Input value={q.relief_requested} onChange={(e) => setQ({ ...q, relief_requested: e.target.value })} placeholder="Compensatory damages, costs, fees" className={inputCls} data-testid="qq-relief" />
        </Field>
      </div>

      <Field label="Summary of What Happened" hint="A short narrative, 3 to 6 sentences. The Drafting step will expand this with AI." testid="field-summary">
        <Textarea
          value={q.summary}
          onChange={(e) => setQ({ ...q, summary: e.target.value })}
          placeholder="On [date], the defendant [did X]. As a result, [plaintiff suffered Y]…"
          className={`${inputCls} min-h-[150px]`}
          data-testid="qq-summary"
        />
      </Field>

      <Field label="Damages Description" hint="How were you harmed? (medical, financial, emotional, reputational, etc.)" testid="field-damages-description">
        <Textarea
          value={q.damages_description}
          onChange={(e) => setQ({ ...q, damages_description: e.target.value })}
          placeholder="Medical expenses of $X; lost wages of $Y; emotional distress; …"
          className={`${inputCls} min-h-[120px]`}
          data-testid="qq-damages-description"
        />
      </Field>

      <div className="flex justify-end pt-2 border-t border-slate-200 mt-8">
        <Button onClick={handleSave} data-testid="save-questionnaire-btn" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">
          Save Questionnaire
        </Button>
      </div>

      <WhyThisMatters step="questionnaire" />
    </div>
  );
}
